import type { PaymentMethodCreateParams, StripeElements, StripeError } from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';
import useConfig from '@sdk/config/useConfig';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import gettext from '@sdk/lang/gettext';
import useToast from '@sdk/common/toast/useToast';

import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { mapBillingAddressToFormValues } from '@web/account/utils/mappers';

export const useStripeUtils = () => {
  const stripe = useStripe();
  const { showErrorToast } = useToast();
  const { APP_URL } = useConfig();
  const { billingInfo } = useBillingInfo();

  const completeStripePayment = async (
    purchaseResult: BillingInfoType,
    elements?: StripeElements
  ): Promise<{ error?: StripeError }> => {
    if (purchaseResult.setupClientSecret) {
      return await stripe.confirmSetup({
        elements,
        clientSecret: purchaseResult.setupClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: APP_URL,
        },
      });
    }

    if (purchaseResult.paymentClientSecret) {
      return await stripe.confirmPayment({
        elements,
        clientSecret: purchaseResult.paymentClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: APP_URL,
          payment_method_data: getCreatePaymentMethodParams(),
        },
      });
    }

    return {};
  };

  const authenticateCard = async (subscription: BillingInfoType) => {
    if (subscription.paymentClientSecret) {
      const scaResult = await stripe.confirmCardPayment(subscription.paymentClientSecret);
      if (scaResult.error && subscription.paymentClientSecret) {
        throw gettext('3D Secure authentication failed');
      }
    }
  };

  const getCreatePaymentMethodParams = (): PaymentMethodCreateParams => {
    return {
      billing_details: mapBillingAddressToFormValues(billingInfo?.billingAddress),
    };
  };

  const createPaymentMethod = async (
    elements: StripeElements,
    paymentFailedClientSecret?: string,
    skipParams?: boolean
  ) => {
    let newPaymentMethodId: string;

    try {
      const { error: elementsFormError } = await elements.submit();
      if (elementsFormError) {
        throw elementsFormError.message;
      }

      if (paymentFailedClientSecret) {
        const { paymentIntent, error } = await stripe.confirmPayment({
          elements,
          clientSecret: paymentFailedClientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: APP_URL,
            payment_method_data: getCreatePaymentMethodParams(),
          },
        });

        if (typeof paymentIntent.payment_method === 'string') {
          newPaymentMethodId = paymentIntent.payment_method;
        } else {
          newPaymentMethodId = paymentIntent.payment_method.id;
        }

        if (error) {
          return Promise.reject(error.message);
        }
      } else {
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          elements,
          // when billing address form is active, we are skipping passing the address
          params: !skipParams ? getCreatePaymentMethodParams() : undefined,
        });
        newPaymentMethodId = paymentMethod.id;
        if (error) {
          return Promise.reject(error.message);
        }
      }
    } catch (e) {
      // Show generic error message to customers and log technical details
      showErrorToast(gettext('Something went wrong'));
      return Promise.reject(e.toString());
    }

    return newPaymentMethodId;
  };

  return {
    completeStripePayment,
    authenticateCard,
    createPaymentMethod,
  };
};
